// usePasswordRecovery + detectRecoveryFromUrl
// -------------------------------------------
// The password-reset flow extracted out of portal.jsx so the URL classification
// and the update-password handlers can be unit-tested. The App keeps the three
// pieces of recovery state and the render screens; this module owns the (pure)
// URL parsing and the two async handlers.
//
// detectRecoveryFromUrl is pure -- it classifies the auth-redirect URL and the
// App performs the side effects (alert / history.replaceState / setState) on the
// result. usePasswordRecovery is a state-bag factory (same pattern as
// useRequestPersistence): it takes the App's setters and returns the handlers.
//
// TypeScript source -- usePasswordRecovery.js is a thin re-export stub.

import { alertDialog } from "../services/dialogService";
import { getSupabaseClient } from "../utils/supabase";

export type RecoveryDetection =
  | { kind: "expired"; alert: string }
  | { kind: "error"; alert: string }
  | { kind: "recovery"; stripResetParam: boolean }
  | { kind: "none" };

// Auth emails carry an 8-digit CODE valid 24 hours and no link (the tokenised link was
// dropped 6 Jul 2026 — corporate mail scanners pre-click links and consume the token).
// This message still described the old one-hour single-use link. Finding B16 item 10.
const EXPIRED_MESSAGE =
  "This password reset request has expired or has already been used.\n\nPlease ask a Zephyr admin to send you a new reset email, then enter the 8-digit code within 24 hours.";

// Classify the auth-redirect URL. Pure: no window / no side effects.
//  - expired : Supabase returned access_denied + otp_expired (used/expired link)
//  - error   : any other Supabase error= response (surface the description)
//  - recovery: a genuine recovery link (type=recovery in hash, or our ?reset=1
//              flag, or type=recovery in the query). stripResetParam tells the
//              caller whether to strip our own ?reset=1 flag from the URL.
//  - none    : not a recovery URL
export function detectRecoveryFromUrl(hash: string, search: string): RecoveryDetection {
  const h = hash || "";
  const s = search || "";
  if (h.indexOf("error=access_denied") >= 0 && h.indexOf("otp_expired") >= 0) {
    return { kind: "expired", alert: EXPIRED_MESSAGE };
  }
  if (h.indexOf("error=") >= 0) {
    const m = h.match(/error_description=([^&]+)/);
    const errDesc = m ? decodeURIComponent(m[1].replace(/\+/g, " ")) : "Authentication error";
    return {
      kind: "error",
      alert: `Sign-in link error: ${errDesc}\n\nPlease request a new password reset email and try again.`,
    };
  }
  const isRecovery =
    h.indexOf("type=recovery") >= 0 || s.indexOf("reset=1") >= 0 || s.indexOf("type=recovery") >= 0;
  if (isRecovery) {
    return { kind: "recovery", stripResetParam: s.indexOf("reset=1") >= 0 };
  }
  return { kind: "none" };
}

export interface PasswordRecoveryDeps {
  pendingNewPassword: string;
  setPasswordRecovery: (v: boolean) => void;
  setRecoveryNeedsMfa: (v: boolean) => void;
  setPendingNewPassword: (v: string) => void;
}

export function usePasswordRecovery(deps: PasswordRecoveryDeps) {
  const { pendingNewPassword, setPasswordRecovery, setRecoveryNeedsMfa, setPendingNewPassword } =
    deps;

  async function submitNewPassword(newPwd: string) {
    if (!newPwd || newPwd.length < 12) {
      await alertDialog({ message: "Password must be at least 12 characters." });
      return;
    }
    const supabaseClient = getSupabaseClient();
    if (!supabaseClient) return;
    const { error } = await supabaseClient.auth.updateUser({ password: newPwd });
    if (error) {
      // Supabase requires AAL2 (post-MFA) before allowing a password change when
      // MFA is enrolled. Recovery tokens give AAL1, so step up via MFA challenge.
      if (/AAL2|MFA/i.test(error.message || "")) {
        setPendingNewPassword(newPwd);
        setRecoveryNeedsMfa(true);
        return;
      }
      await alertDialog({ message: `Could not update password: ${error.message}` });
      return;
    }
    await alertDialog({ message: "Password updated. You are now signed in." });
    setPasswordRecovery(false);
  }

  async function onMfaChallengeSuccessForRecovery() {
    // After a successful MFA challenge, retry the password update.
    setRecoveryNeedsMfa(false);
    const supabaseClient = getSupabaseClient();
    if (!supabaseClient) return;
    const { error } = await supabaseClient.auth.updateUser({ password: pendingNewPassword });
    if (error) {
      await alertDialog({ message: `Could not update password after MFA: ${error.message}` });
      return;
    }
    await alertDialog({ message: "Password updated. You are now signed in." });
    setPendingNewPassword("");
    setPasswordRecovery(false);
  }

  return { submitNewPassword, onMfaChallengeSuccessForRecovery };
}
